import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { CheckCircle2, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { useTranslation } from '../i18n/I18nContext';
import QoldoLogo from './QoldoLogo';
import { useViewportHeight, useLockBodyScroll } from '../hooks/useViewportHeight';

export default function CompletionScreen({ courseId, courseName, clientId, userData, visitorId, onFinish, experienceType }) {
  const { t, tm, lang } = useTranslation();
  
  // iOS Safari viewport height fix
  useViewportHeight();
  useLockBodyScroll(true);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSendFeedback = async () => {
    if (!feedback.trim()) {
      toast.error(t('feedback_enter_first'));
      return;
    }

    setSubmitting(true);
    
    try {
      await api.post(`/courses/${courseId}/feedback`, {
        course_id: courseId,
        feedback_text: feedback.trim(),
        client_id: clientId || '',
        store_id: userData?.store_id || '',
        store_name: userData?.store_name || '',
        learner_name: userData?.user_name || '',
        first_name: userData?.first_name || '',
        last_name: userData?.last_name || '',
        language: lang,
        visitor_id: visitorId || ''
      });
      
      setSubmitted(true);
      toast.success(t('feedback_sent_title'));
      
      // Transition to final closed screen after delay
      setTimeout(() => {
        onFinish();
      }, 2000);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Feedback submission failed');
      // Still allow finishing
      setTimeout(() => {
        onFinish();
      }, 1500);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    // Go directly to final closed screen
    onFinish();
  };

  // Show "Feedback sent" briefly before transitioning
  if (submitted) {
    return (
      <div className="player-completion-root bg-gradient-to-br from-green-900 to-teal-900">
        <div className="player-completion-content">
          <Card className="player-completion-card text-center">
            <CardContent className="py-6">
              <QoldoLogo className="mb-3" />
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <h2 className="text-xl font-bold mb-2">{t('feedback_sent_title')}</h2>
              <p className="text-gray-600 text-sm">
                {t('feedback_sent_body')}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="player-completion-root bg-gradient-to-br from-blue-900 to-purple-900">
      {/* Content area - with scrollable card for long feedback */}
      <div className="player-completion-content">
        <Card className="player-completion-card">
          <CardContent className="py-4 px-4">
            {/* Logo */}
            <QoldoLogo className="mb-3" />
            
            {/* Success Icon - compact */}
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-500 rounded-full mb-2">
                <CheckCircle2 className="h-7 w-7 text-white" />
              </div>
              <h1 className="text-xl font-bold mb-1">{tm('completion_title', experienceType)}</h1>
              <p className="text-sm text-gray-600">
                {tm('completion_subtitle', experienceType, { courseName: courseName || 'the training' })}
              </p>
            </div>

            {/* User Info - compact */}
            {userData && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-3 text-xs">
                <p><strong>{t('completion_name_label')}:</strong> {userData.user_name}</p>
                <p><strong>{t('completion_store_label')}:</strong> {userData.store_name}</p>
              </div>
            )}

            {/* Feedback Section - compact */}
            <div className="border-t pt-3 mb-3">
              <h3 className="text-sm font-semibold mb-1">{t('feedback_title')}</h3>
              <p className="text-xs text-gray-600 mb-2">
                {tm('feedback_help', experienceType)}
              </p>
              
              <Textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={t('feedback_placeholder')}
                rows={3}
                maxLength={2000}
                className="resize-none text-sm"
              />
              
              <p className="text-xs text-gray-500 mt-1">
                {feedback.length} / 2000 {t('feedback_chars')}
              </p>
            </div>

            {/* Actions - stacked */}
            <div className="flex flex-col gap-2">
              <Button
                onClick={handleSendFeedback}
                disabled={submitting || !feedback.trim()}
                className="w-full h-9"
              >
                <Send className="h-4 w-4 mr-2" />
                {submitting ? t('feedback_submitting') : t('feedback_submit')}
              </Button>
              
              <Button
                onClick={handleSkip}
                variant="outline"
                disabled={submitting}
                className="w-full h-9"
              >
                <X className="h-4 w-4 mr-2" />
                {t('feedback_skip')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
